import React, {useEffect, useState} from "react";
import {MemberDTO} from "../../dtos/MemberDTO";
import {NetworkCalls} from "../../NetworkCalls";
import LoadingPageIndicator from "../../components/LoadingPageIndicator";
import uuid from "react-uuid";
import HandleHttpResponses from "../../components/NetworkErrorHandling";
import {AxiosError} from "axios";
import {Link} from "react-router-dom";
import {Button} from "react-bootstrap";
import AddNewMemberDialog from "../../components/AddNewMemberDialog";

export default function MemberList() {
    const [loading, isLoading] = useState<boolean>(false);
    const [members, setMembers] = useState<MemberDTO[]>([]);
    const [error, setError] = useState<AxiosError>();
    const [filteredMembers, setFilteredMembers] = useState<MemberDTO[]>([]);
    const [show, setShow] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const pageSize = 50;

    useEffect(() => {
        loadData();
    }, []);

    const loadData = () => {
        isLoading(true);
        NetworkCalls.getMemberList()
            .then((result) => {
                setMembers(result);
                setFilteredMembers(result);
                isLoading(false);
            })
            .catch((error) => {
                isLoading(false);
                setError(error);
            });
    };

    function search(value: string) {
        const filtered = members.filter(
                (member) =>
                    contains(member.number, value) ||
                    contains(member.lastname, value) ||
                    contains(member.firstname, value) ||
                    contains(member.street, value) ||
                    contains(member.postalCode, value) ||
                    contains(member.city, value)
            );
        setFilteredMembers(filtered);
        setCurrentPage(1);
    }

    function contains(field: string, value: string) {
        return field !== null && field.toUpperCase().includes(value.toUpperCase());
    }

    function showContent() {
        const totalPages = Math.max(1, Math.ceil(filteredMembers.length / pageSize));
        return (
            <div>
                <div className="m-5 col row justify-content-center">
                    <div className="card border-0">
                        <div className="card-header">
                            <div className="row" style={{minWidth: "100%"}}>
                                <div className="col-3">
                                    <input
                                        type="text"
                                        className="form-control"
                                        aria-label="Default"
                                        aria-describedby="inputGroup-sizing-default"
                                        style={{minWidth: "100%"}}
                                        onChange={(elem) => search(elem.target.value.toUpperCase())}
                                    />
                                </div>
                                <div className="col-5 d-flex align-items-center gap-2">
                                    <span className="text-muted">
                                        {filteredMembers.length} Einträge
                                    </span>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        disabled={currentPage <= 1}
                                        className="rounded"
                                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                    >
                                        Zurück
                                    </Button>
                                    <span className="text-muted">
                                        Seite {currentPage} / {totalPages}
                                    </span>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        disabled={currentPage >= totalPages}
                                        className="rounded"
                                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                    >
                                        Weiter
                                    </Button>
                                </div>
                                <div className="col-4 d-flex justify-content-end">
                                    <Button variant="primary" onClick={() => setShow(true)}>
                                        Mitglied hinzufügen
                                    </Button>
                                </div>
                            </div>
                        </div>
                        {showInspectionTable()}
                    </div>
                </div>
                <AddNewMemberDialog
                    show={show}
                    onClose={() => setShow(false)}
                    onMemberSaved={loadData}
                />
            </div>
        );
    }

    function showInspectionTable() {
        const totalPages = Math.max(1, Math.ceil(filteredMembers.length / pageSize));
        const startIndex = (currentPage - 1) * pageSize;
        const pagedMembers = filteredMembers.slice(startIndex, startIndex + pageSize);

        return (
            <>
                <table className="table table-hover">
                    <thead>
                    <tr key={uuid()}>
                        <th scope="col">Mitgliedsnummer</th>
                        <th scope="col">Vorname</th>
                        <th scope="col">Nachname</th>
                        <th scope="col">Adresse</th>
                        <th scope="col">Arbeitsdienstpflichtig</th>
                        <th scope="col"></th>
                        <th scope="col"></th>
                    </tr>
                    </thead>
                    <tbody id="table-content">{createRows(pagedMembers)}</tbody>
                </table>
                <div className="d-flex justify-content-between align-items-center px-3 pb-3">
                    <span className="text-muted">
                        {filteredMembers.length} Einträge
                    </span>
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={currentPage <= 1}
                            className="rounded"
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        >
                            Zurück
                        </Button>
                        <span className="text-muted">
                            Seite {currentPage} / {totalPages}
                        </span>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={currentPage >= totalPages}
                            className="rounded"
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        >
                            Weiter
                        </Button>
                    </div>
                </div>
            </>
        );
    }

    function renderAddress(elem: MemberDTO) {
        if (!!elem.street) {
            return <>{elem.street}, {elem.postalCode} {elem.city}</>;
        }
        return <></>;
    }

    function createRows(list: MemberDTO[]) {
        return list.map((elem) => {
            return (
                <tr key={uuid()} className="user-select-auto">
                    <td>{elem.number}</td>
                    <td> {elem.firstname}</td>
                    <td>{elem.lastname}</td>
                    <td>
                        {renderAddress(elem)}
                    </td>
                    <td>{memberWork(elem.memberWork)}</td>
                    <td>
                        <Link
                            to={`/dashboard/administration/member-list/${elem.number}`}
                            className="btn btn-primary"
                        >
                            Details anzeigen
                        </Link>
                    </td>
                    <td>
                        <Link
                            to={`/dashboard/administration/member-list/${elem.number}/edit`}
                            className="btn btn-primary"
                        >
                            Bearbeiten
                        </Link>
                    </td>
                </tr>
            );
        });
    }

    const memberWork = (memberWork: boolean | undefined) => {
        if (memberWork) {
            return <span className="status status-green">Ja</span>;
        } else {
            return <span className="status status-red">Nein</span>;
        }
    };

    return (
        <div>
            {loading ? (
                <LoadingPageIndicator/>
            ) : error ? (
                HandleHttpResponses(error)
            ) : (
                showContent()
            )}
        </div>
    );
}
